import React, { useState, useEffect } from "react";
import { Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Calendar } from "../components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Reveal } from "../components/motion";
import { BOOKING_PAID_ENABLED } from "../lib/config";

const fmt = (d) => {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const Booking = () => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [sessionType, setSessionType] = useState("discovery");
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const allTypes = t("booking.types");
  const types = BOOKING_PAID_ENABLED ? allTypes : allTypes.filter((ty) => ty.key === "discovery");

  useEffect(() => {
    setSlot(null);
    if (!date) return;
    setLoadingSlots(true);
    api.get(`/bookings/availability?date_str=${fmt(date)}`)
      .then(({ data }) => setSlots(data.slots))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !slot) {
      toast.error(lang === "fr" ? "Choisissez une date et un créneau." : "Choose a date and a slot.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        name: form.name, email: form.email, date: fmt(date), time: slot,
        session_type: sessionType, notes: form.notes, lang,
        origin_url: window.location.origin,
        user_id: user && user !== false ? user.id : null,
      });
      if (data.status === "payment_required" && data.checkout_url) {
        toast.info(t("booking.redirect"));
        window.location.href = data.checkout_url;
      } else {
        setDone(true);
        toast.success(t("booking.success"));
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="booking-success" className="pt-36 pb-28 max-w-2xl mx-auto px-6 text-center min-h-[70vh] flex flex-col justify-center">
        <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-8"><Check className="w-8 h-8" /></div>
        <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-4">{t("booking.success")}</h1>
        <p className="text-foreground/65">{form.email}</p>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="pt-36 pb-28 max-w-[1200px] mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="text-primary/70 text-xs uppercase tracking-[0.25em] mb-6">{t("nav.booking")}</p>
        <h1 className="font-serif text-5xl md:text-7xl tracking-tightest text-foreground mb-4">{t("booking.title")}</h1>
        <p className="text-foreground/65 font-light text-lg max-w-xl mb-14">{t("booking.sub")}</p>
      </Reveal>

      <div className="grid lg:grid-cols-2 gap-12">
        {/* Left: session type + calendar */}
        <div className="space-y-8">
          <div>
            <label className="block text-sm text-foreground/70 mb-3">{t("booking.type")}</label>
            <Select value={sessionType} onValueChange={setSessionType}>
              <SelectTrigger data-testid="session-type-select" className="w-full rounded-full border-border h-12 px-5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {types.map((ty) => (
                  <SelectItem key={ty.key} value={ty.key} data-testid={`session-opt-${ty.key}`}>{ty.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm text-foreground/70 mb-3">{t("booking.date")}</label>
            <div className="border border-border bg-card inline-block rounded-2xl overflow-hidden">
              <Calendar
                data-testid="booking-calendar"
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || d.getDay() === 0 || d.getDay() === 6}
              />
            </div>
          </div>
        </div>

        {/* Right: slots + form */}
        <div className="space-y-8">
          <div>
            <label className="block text-sm text-foreground/70 mb-3">{t("booking.slots")}</label>
            {!date ? (
              <p className="text-foreground/50 text-sm">{t("booking.pickDate")}</p>
            ) : loadingSlots ? (
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            ) : slots.filter((s) => s.available).length === 0 ? (
              <p className="text-foreground/50 text-sm">{t("booking.noSlots")}</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {slots.map((s) => (
                  <button
                    key={s.time}
                    data-testid={`slot-${s.time}`}
                    disabled={!s.available}
                    onClick={() => setSlot(s.time)}
                    className={`py-2.5 text-sm border transition-colors duration-200 ${
                      slot === s.time ? "bg-primary text-primary-foreground border-primary"
                      : s.available ? "border-border text-foreground hover:border-primary" : "border-border/50 text-foreground/30 line-through cursor-not-allowed"
                    }`}
                  >
                    {s.time}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="booking-form">
            <input data-testid="booking-name" required placeholder={t("booking.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-muted px-4 py-3 outline-none focus:ring-1 focus:ring-primary" />
            <input data-testid="booking-email" type="email" required placeholder={t("booking.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-muted px-4 py-3 outline-none focus:ring-1 focus:ring-primary" />
            <textarea data-testid="booking-notes" rows={3} placeholder={t("booking.notes")} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-muted px-4 py-3 outline-none focus:ring-1 focus:ring-primary resize-none" />
            <button data-testid="booking-submit" disabled={submitting} className="w-full bg-primary text-primary-foreground py-4 text-sm font-medium flex items-center justify-center gap-2 hover:bg-primary/90 active:scale-[0.98] transition-[transform,background-color] duration-200 disabled:opacity-60">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t("booking.confirm")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Booking;
